import { Badge, Banner, Box, Button, Group, Heading, Spinner, Text } from '@optiaxiom/react';
import { IconGlobe } from '@optiaxiom/icons';
import { useAppVersions, type AppVersionItem } from '@/lib/api';
import { formatVersionState } from './versionState';
import { formatDate } from './formatDate';
import styles from './AppDetailPage.module.css';

export function VersionsTab({ appId }: { appId: string }) {
  const { data, isLoading, isError, isFetching, refetch } = useAppVersions(appId);

  if (isLoading) {
    return (
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Spinner />
        <Text color="fg.secondary">Loading versions…</Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Heading level="2" fontWeight="700">
          Couldn&rsquo;t load versions
        </Heading>
        <Text color="fg.secondary">Something went wrong loading this app&rsquo;s versions.</Text>
        <Button appearance="primary" onClick={() => void refetch()} loading={isFetching} disabled={isFetching}>
          Try again
        </Button>
      </Group>
    );
  }

  const degradedShards = data?.degradedShards ?? [];

  // A version is "live" when it's running on any shard. Surface running versions first,
  // keeping the server's latest-updated-first order within each group (sort is stable).
  const isVersionRunning = (version: AppVersionItem) =>
    Object.values(version.shards).some((shard) => shard.state === 'RUNNING');
  const items = [...(data?.items ?? [])].sort(
    (a, b) => Number(isVersionRunning(b)) - Number(isVersionRunning(a)),
  );

  if (items.length === 0) {
    return (
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Heading level="2" fontWeight="700">
          No versions yet
        </Heading>
        <Text color="fg.secondary">
          Versions appear here once you scaffold and publish your app with the OCP CLI.
        </Text>
      </Group>
    );
  }

  return (
    <Group flexDirection="column" gap="12" alignItems="stretch">
      {degradedShards.length > 0 && (
        <Banner intent="warning" rounded="lg">
          <Text>
            Some regions were unavailable ({degradedShards.join(', ')}); this list may be incomplete.
          </Text>
        </Banner>
      )}

      {items.map((version) => {
        const isRunning = isVersionRunning(version);
        return (
          <Box
            key={version.version}
            border="1"
            rounded="lg"
            p="16"
            className={isRunning ? styles.runningCard : styles.versionCard}
          >
            <Group flexDirection="row" justifyContent="space-between" alignItems="center" gap="12">
              <Badge intent="neutral" rounded="md" variant="subtle" fontFamily="mono" fontWeight="600" textTransform="none" fontSize="md">
                v{version.version}
              </Badge>
              <Text color="fg.secondary" fontSize="sm">
                {formatDate(version.latestUpdatedAt)}
              </Text>
            </Group>

            <Group flexDirection="row" alignItems="center" gap="6" flexWrap="wrap" mt="12">
              <Box asChild color="fg.tertiary">
                <span>
                  <IconGlobe size={14} />
                </span>
              </Box>
              <Text color="fg.secondary" fontSize="sm">
                Region:
              </Text>
              {Object.entries(version.shards).map(([shardId, shardState], index) => {
                const display = formatVersionState(shardState.state);
                const isLast = index === Object.keys(version.shards).length - 1;
                return (
                  <Group key={shardId} flexDirection="row" alignItems="center" gap="6">
                    <Text fontSize="sm" fontWeight="600" textTransform="uppercase">
                      {shardId}
                    </Text>
                    <Badge intent={display.intent} variant="subtle" fontSize="xs">
                      {display.label}
                    </Badge>
                    {!isLast && <Text color="fg.tertiary">/</Text>}
                  </Group>
                );
              })}
            </Group>
          </Box>
        );
      })}
    </Group>
  );
}
